import { atom, useAtom } from 'jotai';
import { once } from 'lodash';
import React, {
    type ComponentType,
    createContext,
    type ReactNode,
    useContext,
    useMemo,
    useState,
} from 'react';

import type { DeepPartial } from '../types/DeepPartial';
import type { Logger } from '../types/Logger';
import type { Membership, User } from '../types/Membership';
import { MockLogger } from '../util/MockLogger';

interface FlowContextType<TData> {
    data: DeepPartial<TData>;
    setData: (data: DeepPartial<TData>) => void;
    index: number;
    setIndex: (index: number) => void;
    isSubmitting: boolean;
    setIsSubmitting: (isSubmitting: boolean) => void;
    membership: Membership;
    currentUser?: User;
    onComplete: (data: TData) => Promise<void>;
    onExit: () => void;
    onError: (exception: Error) => void;
    logger: Logger;
}

const createStateContext = once(<TData,>() =>
    createContext(undefined as FlowContextType<TData> | undefined),
);

export const useFlow = <TData,>(): FlowContextType<TData> => {
    const context = useContext(createStateContext<TData>());

    if (context == null) {
        throw new Error('useFlow must be used within a FlowProvider.');
    }

    return context;
};

interface FlowProviderProps<TData> {
    existingData: DeepPartial<TData>;
    membership: Membership;
    currentUser?: User;
    onComplete: (data: TData) => Promise<void>;
    onExit: () => void;
    onError: (exception: Error) => void;
    logger?: Logger;
    children: ReactNode;
}

const FlowProvider = <TData,>({
    existingData,
    membership,
    currentUser,
    onComplete,
    onExit,
    onError,
    logger = MockLogger,
    children,
}: FlowProviderProps<TData>) => {
    const FlowContext = createStateContext<TData>();

    // When at atom is created in a render function, we need to wrap it in useMemo.
    // Docs: https://jotai.org/docs/core/atom#note-about-creating-an-atom-in-render-function
    const dataAtom = useMemo(() => {
        return atom(existingData);
    }, [JSON.stringify(existingData)]);

    const [data, setData] = useAtom(dataAtom);
    const [index, setIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    return (
        <FlowContext.Provider
            value={{
                data,
                setData,
                index,
                setIndex,
                isSubmitting,
                setIsSubmitting,
                membership,
                currentUser,
                onComplete,
                onExit,
                onError,
                logger,
            }}
        >
            {children}
        </FlowContext.Provider>
    );
};

export type WrappedFlowProps<TData, TProps> = Omit<
    FlowProviderProps<TData>,
    'children'
> &
    TProps;

export const withFlowProvider =
    <TData, TProps = React.JSX.IntrinsicAttributes>(
        Component: ComponentType<TProps>,
    ) =>
    ({
        existingData,
        membership,
        currentUser,
        onComplete,
        onExit,
        onError,
        logger,
        ...props
    }: WrappedFlowProps<TData, TProps>) => {
        return (
            <FlowProvider
                existingData={existingData}
                membership={membership}
                currentUser={currentUser}
                onComplete={onComplete}
                onExit={onExit}
                onError={onError}
                logger={logger}
            >
                <Component
                    {...(props as unknown as TProps &
                        React.JSX.IntrinsicAttributes)}
                />
            </FlowProvider>
        );
    };
